import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Loader2, Mail, Lock, User } from "lucide-react";
import { Header } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") await login(email.trim(), password);
      else await register(email.trim(), password, name.trim());
      toast.success("Welcome to NightOut!");
      navigate("/my-nights");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="relative overflow-hidden min-h-[calc(100vh-64px)] flex items-center">
        <div className="pointer-events-none absolute -top-20 left-1/2 h-96 w-96 -translate-x-1/2 rounded-full bg-primary/20 blur-[120px]" />
        <div className="relative mx-auto w-full max-w-md px-4 py-16">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl border border-border bg-card p-8 shadow-2xl glow"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={18} className="neon-magenta" />
              <h1 className="font-display text-2xl font-extrabold tracking-tight">
                {mode === "login" ? "Welcome back" : "Create account"}
              </h1>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              {mode === "login" ? "Log in to save and revisit your nights." : "Save your nights and pick up where you left off."}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-secondary/50 p-1">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => setMode(m)}
                  className={`rounded-lg py-2 text-sm font-semibold capitalize transition-colors ${
                    mode === m ? "cta-gradient text-white" : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {m === "login" ? "Log in" : "Sign up"}
                </button>
              ))}
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4">
              {mode === "register" && (
                <div className="relative">
                  <User size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="h-12 pl-10 rounded-xl"
                  />
                </div>
              )}
              <div className="relative">
                <Mail size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="h-12 pl-10 rounded-xl"
                />
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="auth-password-input"
                  type="password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="h-12 pl-10 rounded-xl"
                />
              </div>
              <Button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={loading}
                className="h-12 w-full rounded-xl cta-gradient text-white font-bold hover:opacity-90"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : mode === "login" ? "Log in" : "Create account"}
              </Button>
            </form>
          </motion.div>
        </div>
      </main>
    </>
  );
}
